import { toEnglishDigits } from './arabicNumerals';

const isFormField = (target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement => {
  const el = target as HTMLElement | null;
  return Boolean(el) && (el!.tagName === 'INPUT' || el!.tagName === 'TEXTAREA');
};

// React overrides the native `value` property setter on <input>/<textarea>
// elements to track whether a change came from a real user event or a
// plain script assignment. When we have to fabricate our own 'input' event
// (because the native one was prevented — see the beforeinput handler
// below), a plain `field.value = x` bypasses that tracker: the DOM looks
// right immediately, but React's internally-tracked value silently stays
// out of sync, and gets reasserted (wiping our correction) on the next
// render. Calling the *native* setter directly — retrieved here before
// anything overrides it — keeps React's tracker in sync so the value
// actually sticks. This is the standard technique for scripting a value
// change into a React-controlled input.
const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
  window.HTMLInputElement.prototype,
  'value',
)?.set;
const nativeTextareaValueSetter = Object.getOwnPropertyDescriptor(
  window.HTMLTextAreaElement.prototype,
  'value',
)?.set;

const setFieldValue = (field: HTMLInputElement | HTMLTextAreaElement, value: string): void => {
  const setter = field instanceof HTMLTextAreaElement ? nativeTextareaValueSetter : nativeInputValueSetter;
  if (setter) {
    setter.call(field, value);
  } else {
    field.value = value;
  }
};

// Live-converts Arabic-Indic (٠-٩) and Persian (۰-۹) digits typed into any
// <input>/<textarea> to plain English digits, app-wide — so admins never end
// up submitting Arabic-digit values, regardless of keyboard layout or OS
// input method, without every form needing its own fix.
export const setupArabicDigitInputNormalization = (): void => {
  // <input type="number"> (and similar) rejects non-ASCII-digit characters
  // natively, BEFORE the 'input' event ever fires — typing an Arabic digit
  // into one fires 'beforeinput' with the raw character, then the browser
  // silently drops it (no 'input' event at all), so a plain 'input'
  // listener never gets a chance to correct it. Intercepting at
  // 'beforeinput' runs earlier: convert the digits in the pending insertion,
  // prevent the native (would-be-rejected) insertion, and splice the
  // already-valid ASCII text in ourselves so the number input never sees
  // the character it would have rejected.
  document.addEventListener(
    'beforeinput',
    (event) => {
      const inputEvent = event as InputEvent;
      const target = inputEvent.target;
      if (!isFormField(target)) return;
      // Only handle direct typed/pasted text; let IME composition sequences
      // pass through natively — composed text still gets corrected by the
      // 'input' listener below once composition commits.
      if (inputEvent.inputType !== 'insertText' && inputEvent.inputType !== 'insertFromPaste') return;

      const data = inputEvent.data;
      if (!data) return;
      const converted = toEnglishDigits(data);
      if (converted === data) return;

      inputEvent.preventDefault();

      const field = target;
      const start = field.selectionStart ?? field.value.length;
      const end = field.selectionEnd ?? field.value.length;
      setFieldValue(field, field.value.slice(0, start) + converted + field.value.slice(end));

      const newCursor = start + converted.length;
      try {
        field.setSelectionRange(newCursor, newCursor);
      } catch (e) {
        // ignore — unsupported for this input type
      }

      // The native insertion was prevented, so the browser won't fire its
      // own 'input' event for this keystroke — dispatch one so React's
      // controlled-input onChange still runs and picks up the new value.
      field.dispatchEvent(new Event('input', { bubbles: true }));
    },
    true,
  );

  // Catch-all for anything beforeinput doesn't cover (drag-drop, autofill,
  // programmatic value changes from other scripts, composed IME text, and
  // browsers where the field type didn't natively reject the character in
  // the first place — e.g. type="text"/"email"/"tel", textarea).
  //
  // Runs on 'input' in the CAPTURE phase (on document), which fires while
  // the event is still travelling down to its target — i.e. before React's
  // own delegated onChange listener (attached at the root container, bubble
  // phase) ever sees it. By the time the event reaches and bubbles back up
  // through React's listener, target.value has already been corrected, so
  // React's controlled-input onChange receives the converted value directly.
  document.addEventListener(
    'input',
    (event) => {
      const target = event.target;
      if (!isFormField(target)) return;

      const field = target;
      const original = field.value;
      if (typeof original !== 'string') return;

      const converted = toEnglishDigits(original);
      if (converted === original) return;

      // Some input types (number, email, ...) don't support selection
      // ranges and throw on access — best-effort cursor preservation only.
      let selectionStart: number | null = null;
      let selectionEnd: number | null = null;
      try {
        selectionStart = field.selectionStart;
        selectionEnd = field.selectionEnd;
      } catch (e) {
        // ignore — unsupported for this input type
      }

      field.value = converted;

      if (selectionStart !== null && selectionEnd !== null) {
        try {
          field.setSelectionRange(selectionStart, selectionEnd);
        } catch (e) {
          // ignore — unsupported for this input type
        }
      }
    },
    true,
  );
};

const convertToLocaleSafeNumberInput = (el: Element): void => {
  if (!(el instanceof HTMLInputElement) || el.type !== 'number') return;
  // type="text" always renders its value verbatim — no browser reformats
  // plain text by locale the way it reformats a native number input's
  // displayed digits. inputMode="numeric" keeps the numeric keyboard on
  // touch devices, matching what type="number" gave on mobile.
  el.type = 'text';
  el.inputMode = 'numeric';
};

// Chromium renders <input type="number"> using the browser/device's own
// language's digit system (e.g. Arabic-Indic ٠-٩) whenever that's Arabic —
// independent of this page's own `lang="en"` on <html>, and independent of
// the field's actual (always-English) stored value.
//
// Setting `lang="en"` on the input — the commonly cited workaround — does
// NOT reliably override this: confirmed still broken even after a full
// device restart with that fix in place. The only reliable fix is to avoid
// Blink's native number-input renderer entirely by using type="text"
// instead (see above). This does drop the browser's native per-keystroke
// rejection of non-numeric characters that type="number" provided — typing
// a letter into one of these fields no longer gets silently blocked, it
// just fails the field's existing Yup `number()` validation on blur/submit
// instead, same as any other text field with numeric validation.
//
// Applied globally — to every existing number input plus any added later
// (e.g. inside a modal that mounts after this runs) — so no individual
// form needs its own fix.
export const setupEnglishNumberInputDisplay = (): void => {
  document.querySelectorAll('input[type="number"]').forEach(convertToLocaleSafeNumberInput);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('input[type="number"]')) convertToLocaleSafeNumberInput(node);
        node.querySelectorAll?.('input[type="number"]').forEach(convertToLocaleSafeNumberInput);
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
};
